import React, { useCallback, useEffect, useState } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import { isNativeShell } from '../auth/authRedirect';
import './native-version-gate.css';

const APP_STORE_URL = 'https://apps.apple.com/app/line-medic/id6806487617';
const BYPASS_PATHS = new Set(['/privacy', '/support', '/app-review-demo', '/control']);

function macVersion() {
  if (typeof navigator === 'undefined') return null;
  const match = String(navigator.userAgent || '').match(/LineMedicMac\/([0-9]+(?:\.[0-9]+){0,2})/i);
  return match ? { platform: 'macos', version: match[1], build: 0 } : null;
}

function compareVersion(left, right) {
  const a = String(left || '0').split('.').map(value => Number(value) || 0);
  const b = String(right || '0').split('.').map(value => Number(value) || 0);
  const length = Math.max(a.length, b.length, 3);
  for (let index = 0; index < length; index += 1) {
    const av = a[index] || 0;
    const bv = b[index] || 0;
    if (av !== bv) return av < bv ? -1 : 1;
  }
  return 0;
}

function requiresUpdate(installed, policy) {
  if (!policy?.mandatoryUpgrade) return false;
  const versionResult = compareVersion(installed?.version, policy.minimumVersion);
  if (versionResult < 0) return true;
  if (versionResult > 0) return false;
  const installedBuild = Number(installed?.build || 0);
  const minimumBuild = Number(policy?.minimumBuild || 0);
  return minimumBuild > 0 && installedBuild > 0 && installedBuild < minimumBuild;
}

async function installedNativeVersion() {
  const mac = macVersion();
  if (mac) return mac;
  if (!isNativeShell()) return null;
  try {
    const info = await CapacitorApp.getInfo();
    return {
      platform: 'ios',
      version: String(info?.version || ''),
      build: Number(info?.build || 0),
    };
  } catch (_) {
    return { platform: 'ios', version: '', build: 0 };
  }
}

export default function NativeVersionGate({ children }) {
  const [state, setState] = useState({ status: 'checking', installed: null, policy: null });

  const check = useCallback(async () => {
    const path = typeof window === 'undefined' ? '/' : (window.location.pathname.replace(/\/+$/, '') || '/');
    if (BYPASS_PATHS.has(path)) {
      setState({ status: 'allowed', installed: null, policy: null });
      return;
    }

    const installed = await installedNativeVersion();
    if (!installed) {
      setState({ status: 'allowed', installed: null, policy: null });
      return;
    }

    try {
      const response = await fetch(`/api/app-version?platform=${encodeURIComponent(installed.platform)}`, {
        cache: 'no-store',
      });
      const policy = await response.json().catch(() => null);
      if (!response.ok || !policy) throw new Error('Version policy unavailable.');

      setState({
        status: requiresUpdate(installed, policy) ? 'blocked' : 'allowed',
        installed,
        policy,
      });
    } catch (_) {
      // Fail open for a previously installed supported app when the policy service is unreachable.
      setState({ status: 'allowed', installed, policy: null });
    }
  }, []);

  useEffect(() => {
    check();
    const online = () => check();
    const visible = () => { if (document.visibilityState === 'visible') check(); };
    window.addEventListener('online', online);
    document.addEventListener('visibilitychange', visible);
    return () => {
      window.removeEventListener('online', online);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [check]);

  if (state.status === 'blocked') {
    const policy = state.policy || {};
    return (
      <main className="native-version-gate">
        <section className="native-version-card">
          <div className="native-version-kicker">LINE MEDIC UPDATE REQUIRED</div>
          <h1>Update Line Medic to continue</h1>
          <p>{policy.message || 'A newer version of Line Medic is required.'}</p>
          <div className="native-version-meta">
            <span>Installed: {state.installed?.version || 'Unknown'}{state.installed?.build ? ` (${state.installed.build})` : ''}</span>
            <span>Required: {policy.minimumVersion || 'Current'}{policy.minimumBuild ? ` (${policy.minimumBuild})` : ''}</span>
          </div>
          <button type="button" onClick={() => window.location.assign(policy.appStoreUrl || APP_STORE_URL)}>
            Update from the App Store
          </button>
          <small>After the update finishes, reopen Line Medic.</small>
        </section>
      </main>
    );
  }

  return children;
}

export { compareVersion, requiresUpdate };
